import type { ScheduleResult } from "@planerius/contracts/cabinet"

import { shiftDate, todayInZone } from "../../../lib/format"

/**
 * 2-2.10.4 «Мастер выбывает» — один сценарий на «заболела» и «уволилась».
 *
 * ПОЧЕМУ ПРЕДПРОСМОТР СЧИТАЕТСЯ ЗДЕСЬ, А НЕ НА СЕРВЕРЕ. Макет обещает три плитки ДО
 * применения: сколько записей встанут без изменений, сколько попадут вне смены
 * принимающего и у скольких он не делает услугу. У POST /v1/cabinet/reassign-day
 * сухого прогона нет — conflicts[] приходит ПОСЛЕ мутации. Это названный разрыв этапа.
 *
 * Но обещание макета выполнимо и без него: журнал периода уже отдаёт и записи выбывающего,
 * и смены принимающего, а справочник сотрудников — список его услуг. Поэтому предпросмотр
 * СЧИТАЕТСЯ из тех же данных, что видит человек на экране, и это честно ровно в той мере,
 * в какой честен сам журнал: если за секунду до применения кто-то занял время, сервер
 * вернёт конфликт, и его показывают списком. Предпросмотр — обещание, итог — факт.
 *
 * ГЛАВНОЕ, ЧТО НЕЛЬЗЯ СЛОМАТЬ МОЛЧА: три группы НЕ ПЕРЕСЕКАЮТСЯ. Запись, которую
 * принимающий не умеет делать, не должна попасть ещё и в «встанут без изменений» —
 * иначе сумма плиток перестанет сходиться с числом записей, и владелец применит
 * решение, поверив в неверную арифметику.
 */

export type LeavePeriodKind = "today" | "until_friday" | "until_date" | "forever"

/** Что делать с записями. Порядок — из макета, первый выбран по умолчанию. */
export type LeaveAction = "reassign" | "unassign" | "cancel"

/** Что сказать клиентам. */
export type LeaveNotify = "none" | "template" | "self_reschedule"

export interface LeavePeriod {
  readonly kind: LeavePeriodKind
  readonly dateFrom: string
  /** Для «навсегда» — горизонт, дальше которого записей просто нет. */
  readonly dateTo: string
}

/**
 * Период → календарные даты.
 *
 * Горизонт запроса журнала — 31 день (ScheduleQuery), поэтому «навсегда» здесь означает
 * «весь горизонт, который вообще можно посмотреть за один запрос». Записи дальше него
 * закрывает сама архивация мастера — она не ограничена датой.
 */
export const MAX_PERIOD_DAYS = 31

export function resolvePeriod(kind: LeavePeriodKind, today: string, untilDate?: string): LeavePeriod {
  switch (kind) {
    case "today":
      return { kind, dateFrom: today, dateTo: today }
    case "until_friday":
      return { kind, dateFrom: today, dateTo: nextFriday(today) }
    case "until_date":
      return {
        kind,
        dateFrom: today,
        dateTo: untilDate ?? shiftDate(today, MAX_PERIOD_DAYS - 1),
      }
    case "forever":
      return { kind, dateFrom: today, dateTo: shiftDate(today, MAX_PERIOD_DAYS - 1) }
  }
}

/** Ближайшая пятница ВКЛЮЧИТЕЛЬНО: «до пятницы», сказанное в четверг, — это два дня. */
export function nextFriday(today: string): string {
  const parsed = new Date(`${today}T12:00:00Z`)
  const weekday = parsed.getUTCDay()
  const ahead = (5 - weekday + 7) % 7
  return shiftDate(today, ahead)
}

export interface LeaveAppointmentRow {
  readonly id: string
  readonly clientName: string
  readonly startAt: string
  readonly date: string
  /** Названия услуг — нужны плитке «услугу не выполняет». */
  readonly serviceNames: readonly string[]
  /** Услуги, которых у принимающего нет. Пусто — все умеет. */
  readonly missingServiceNames: readonly string[]
  readonly fitsShift: boolean
}

export interface LeavePreview {
  /** Все будущие записи выбывающего за период. */
  readonly all: readonly LeaveAppointmentRow[]
  /** Встанут к принимающему без изменений. */
  readonly fits: readonly LeaveAppointmentRow[]
  /** Попадут вне смены принимающего. */
  readonly outsideShift: readonly LeaveAppointmentRow[]
  /** Принимающий не делает такую услугу. */
  readonly serviceMissing: readonly LeaveAppointmentRow[]
}

/** Статусы, которые вообще надо куда-то девать. Прошлое и отменённое не трогаем. */
const LIVE_STATUSES = new Set(["pending", "confirmed", "in_progress"])

export interface LeavePreviewInput {
  readonly result: ScheduleResult
  readonly fromEmployeeId: string
  /** Кому передаём. null — «оставить без мастера»: тогда считать нечего. */
  readonly toEmployeeId: string | null
  /** Услуги принимающего. Пусто — он не делает ничего из каталога. */
  readonly toEmployeeServiceIds: ReadonlySet<string>
  readonly period: LeavePeriod
}

export function buildLeavePreview(input: LeavePreviewInput): LeavePreview {
  const timeZone = input.result.branch.timezone

  const mine = input.result.appointments.filter(
    (appointment) =>
      appointment.employeeId === input.fromEmployeeId && LIVE_STATUSES.has(appointment.status),
  )

  // Смены принимающего по датам. Запись «встанет без изменений», только если её окно
  // целиком внутри смены: половина стрижки за пределами рабочего дня — это не «встанет».
  const targetWorkdays = input.result.workdays.filter(
    (workday) => workday.employeeId === input.toEmployeeId,
  )

  const rows = mine
    // Период отсекается ещё раз здесь, а не только в запросе журнала: журнал берут
    // одним запросом на весь горизонт и переключают чипы периода без похода на сервер,
    // и «до пятницы» обязано означать до пятницы, а не «сколько загрузилось».
    .filter((appointment) => {
      const date = todayInZone(timeZone, new Date(appointment.startAt))
      return date >= input.period.dateFrom && date <= input.period.dateTo
    })
    .map((appointment): LeaveAppointmentRow => {
      const date = todayInZone(timeZone, new Date(appointment.startAt))
      const serviceNames = appointment.services.map((service) => service.name)

      const missing =
        input.toEmployeeId === null
          ? []
          : appointment.services
              .filter((service) => !input.toEmployeeServiceIds.has(service.serviceId))
              .map((service) => service.name)

      const fitsShift =
        input.toEmployeeId === null
          ? false
          : targetWorkdays.some(
              (workday) =>
                workday.date === date &&
                new Date(workday.startAt).getTime() <= new Date(appointment.startAt).getTime() &&
                new Date(workday.endAt).getTime() >= new Date(appointment.endAt).getTime(),
            )

      return {
        id: appointment.id,
        clientName: appointment.client.displayName,
        startAt: appointment.startAt,
        date,
        serviceNames,
        missingServiceNames: missing,
        fitsShift,
      }
    })

  // Группы не пересекаются, и порядок разбора — это приоритет: «не делает услугу»
  // сильнее «вне смены», потому что смену подвинуть можно, а умение — нет.
  const serviceMissing = rows.filter((row) => row.missingServiceNames.length > 0)
  const rest = rows.filter((row) => row.missingServiceNames.length === 0)
  const outsideShift = rest.filter((row) => !row.fitsShift)
  const fits = rest.filter((row) => row.fitsShift)

  return { all: rows, fits, outsideShift, serviceMissing }
}

/** Названия услуг, которых не умеет принимающий, — без повторов и в порядке встречи. */
export function missingServiceNames(preview: LeavePreview): readonly string[] {
  const seen = new Set<string>()
  for (const row of preview.serviceMissing) {
    for (const name of row.missingServiceNames) seen.add(name)
  }
  return [...seen]
}

export interface LeaveSummaryInput {
  readonly preview: LeavePreview
  readonly action: LeaveAction
  readonly notify: LeaveNotify
  readonly toEmployeeName: string | null
  readonly fromEmployeeName: string
  readonly periodKind: LeavePeriodKind
}

export interface LeaveSummary {
  /** Сколько реально переедет к принимающему. */
  readonly movedCount: number
  /** Сколько останется разбирать по одной. */
  readonly leftoverCount: number
  readonly notifyCount: number
  /** Абзац «Итог до применения». */
  readonly text: string
  /** Подпись кнопки применения. */
  readonly applyLabel: string
}

function periodWords(kind: LeavePeriodKind): string {
  switch (kind) {
    case "today":
      return "на сегодня"
    case "until_friday":
      return "до пятницы"
    case "until_date":
      return "до выбранной даты"
    case "forever":
      return "навсегда"
  }
}

function appointmentWord(count: number): string {
  const mod100 = count % 100
  if (mod100 >= 11 && mod100 <= 14) return "записей"
  const mod10 = count % 10
  if (mod10 === 1) return "запись"
  if (mod10 >= 2 && mod10 <= 4) return "записи"
  return "записей"
}

export function buildLeaveSummary(input: LeaveSummaryInput): LeaveSummary {
  const total = input.preview.all.length
  const moved = input.action === "reassign" ? input.preview.fits.length : 0
  const leftover = total - moved
  const notifyCount = input.notify === "none" ? 0 : total

  const target = input.toEmployeeName ?? "другому мастеру"

  const parts: string[] = []
  if (input.action === "reassign") {
    parts.push(`${moved} ${appointmentWord(moved)} передаются: ${target}`)
    if (leftover > 0) parts.push(`${leftover} остаются в списке на разбор`)
  }
  if (input.action === "unassign") {
    parts.push(`${total} ${appointmentWord(total)} уйдут в колонку «Без мастера»`)
  }
  if (input.action === "cancel") {
    parts.push(`${total} ${appointmentWord(total)} будут отменены салоном`)
  }

  parts.push(
    notifyCount === 0
      ? "уведомления клиентам не уйдут — вы предупредите их сами"
      : `${notifyCount} уведомлений уйдут клиентам`,
  )
  parts.push(
    `смены ${input.fromEmployeeName} ${periodWords(input.periodKind)} закроются отсутствием, чтобы на них не записались онлайн`,
  )

  const applyLabel =
    input.action === "reassign"
      ? leftover > 0
        ? `Применить · ${moved} перенести, ${leftover} на разбор`
        : `Применить · ${moved} перенести`
      : input.action === "unassign"
        ? `Применить · ${total} без мастера`
        : `Применить · отменить ${total}`

  return {
    movedCount: moved,
    leftoverCount: leftover,
    notifyCount,
    text: `${parts.join(" · ")}. Результат покажем списком, а не числом.`,
    applyLabel,
  }
}

/**
 * Шаблон уведомления из макета.
 *
 * ИМЕНА ОСТАЮТСЯ ПОДСТАНОВКАМИ, а не вставляются здесь, и это не лень. Русский текст
 * шаблона требует падежей — «у Анны», «к мастеру Дмитрию», — а склонять имена в браузере
 * нечем: «у Мельниченко-Орлова» и «к мастеру Ким» одинаково неверны. Подстановки
 * раскрывает шаблонизатор салона, который знает и падеж, и род. Администратор при этом
 * видит ровно тот текст, который уйдёт клиенту, с явно обозначенными пропусками.
 */
/**
 * ЧТО НА САМОМ ДЕЛЕ ПОЛУЧИТ КЛИЕНТ ПРИ ЗАМЕНЕ МАСТЕРА.
 *
 * Здесь стоял готовый текст сообщения — «Ваша запись на {дата} у {мастер} переносится
 * к мастеру {замена}. Время прежнее», — и его не отправлял никто: своего вида события
 * у замены мастера нет, шаблона тоже, а подписчика outbox, на который ссылался сервер,
 * не существует. Обещание сообщения страшнее его отсутствия: администратор, поверивший
 * экрану, НЕ ЗВОНИТ клиентам — и они приезжают к мастеру, которого не ждали.
 *
 * Поэтому здесь теперь написано то, что произойдёт: напоминание за сутки и за два часа
 * уйдёт уже с новым мастером (план перестраивается при переносе), а отдельного сообщения
 * «вас передали» не будет, пока для него не появится событие и шаблон.
 */
export const NOTIFY_TEMPLATE =
  "Отдельного сообщения «вас передали другому мастеру» пока нет: для него нужны своё " +
  "событие и шаблон. Напоминания за сутки и за два часа уйдут уже с новым мастером. " +
  "Тем, кому важно знать заранее, позвоните."
